import Animations, { AnimationVariant, AnimationVariants } from '@assets/lottie'
import { FC, Suspense, lazy, useEffect, useRef, useState } from 'react'

const Player = lazy(async () => {
  const mod = await import('@lottiefiles/react-lottie-player')
  // For compatibility: some installations use `default`, some do not.
  return { default: mod.Player }
})

const DEFAULT_ANIMATION_DURATION = 1000 * 3

type AnimationProps = {
  isVisible: boolean
  variant?: AnimationVariant
  duration?: number
  onClose?: () => void
}
const Animation: FC<AnimationProps> = ({
  variant,
  isVisible,
  onClose,
  duration = DEFAULT_ANIMATION_DURATION,
}) => {
  const refTimeout = useRef<number | null>(null)

  const [animationVariant, setAnimationVariant] = useState<AnimationVariant>()
  const handleClose: React.AnimationEventHandler = _event => {
    !!onClose && onClose()
  }

  useEffect(() => {
    if (isVisible) {
      // randomize new variant if not specified
      if (!!variant) {
        setAnimationVariant(variant)
      } else {
        setRandomizedVariant()
      }

      // set a timeout for how long we want to display this for
      if (refTimeout.current) {
        clearTimeout(refTimeout.current)
      }
      refTimeout.current = setTimeout(handleClose, duration)

      return () => {
        if (!!refTimeout.current) {
          clearTimeout(refTimeout.current)
        }
      }
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, variant])

  const setRandomizedVariant = () => {
    const randomVariantIndex = Math.floor(Math.random() * AnimationVariants.length)
    setAnimationVariant(AnimationVariants[randomVariantIndex])
  }

  // Unmount when isVisible=false so Lottie does not keep looping behind the fade
  // leading to `RangeError: Maximum call stack size exceeded` in iOS WebKit.
  // Wrap in Suspense to keeps the lazy Player chunk from remounting the parent route.
  return (
    isVisible &&
    animationVariant && (
      <Suspense fallback={null}>
        <Player key={`${refTimeout.current}`} autoplay loop src={Animations[animationVariant]} />
      </Suspense>
    )
  )
}

export default Animation
